import { useEffect, useRef, type CSSProperties } from "react";
import type { Game } from "@engine/core/Game";

interface Props {
  game?: Game | undefined;
  /** Side length in CSS pixels. Default 160. */
  size?: number;
  /** Half-window in WORLD pixels — what fraction of the world fits in the minimap. Default 480. */
  worldRadius?: number;
  /** Background color (hex). Default 0x0a0e1c. */
  bg?: number;
}

const wrapStyle = (size: number): CSSProperties => ({
  position: "fixed",
  top: 8,
  right: 300,
  width: size,
  height: size,
  background: "rgba(0,0,0,0.55)",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: "50%",
  overflow: "hidden",
  zIndex: 990,
  pointerEvents: "none",
});

const KIND_COLORS: Record<string, string> = {
  player: "#60a5fa",
  remotePlayer: "#a855f7",
  npc: "#facc15",
  monster: "#ef4444",
  audio_emitter: "#4ade80",
  portal: "#f472b6",
};

/**
 * Player-centered minimap. The local player sits at the dead center of the
 * disc; everything else (other players, mobs, NPCs, exits, bosses) rotates
 * around as you move. World boundary is drawn as a heavy outline so you can
 * see when you're near the zone edge.
 *
 * Reads positions from `game.inspector`'s entity-provider (which the active
 * scene wires to its ECS world) and the `localControlled` flag picks "me".
 *
 * Cheap: a single canvas redrawn at 10 Hz.
 */
export function WorldMinimap({ game, size = 160, worldRadius = 480, bg = 0x0a0e1c }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!game) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const bgHex = `#${bg.toString(16).padStart(6, "0")}`;
    const half = size / 2;
    const scale = half / worldRadius; // world units per minimap px

    let raf = 0;
    let last = 0;
    const tick = (ts: number): void => {
      raf = requestAnimationFrame(tick);
      if (ts - last < 100) return;
      last = ts;

      ctx.clearRect(0, 0, size, size);
      // Disc background (only inside the circle so corners stay transparent).
      ctx.save();
      ctx.beginPath();
      ctx.arc(half, half, half - 1, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.fillStyle = bgHex;
      ctx.fillRect(0, 0, size, size);

      // Find local player
      let player: { x: number; y: number } | null = null;
      const provider = (game.inspector as unknown as { provider?: () => Iterable<unknown> }).provider;
      if (typeof provider === "function") {
        for (const e of provider()) {
          const ent = e as { localControlled?: true; position?: { x: number; y: number } };
          if (ent.localControlled && ent.position) {
            player = ent.position;
            break;
          }
        }
      }

      if (!player) {
        ctx.restore();
        return;
      }

      // Draw N-arrow (always points up regardless of camera rotation)
      // Subtle compass tick at the top
      ctx.strokeStyle = "rgba(255,255,255,0.18)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(half, 4);
      ctx.lineTo(half, 12);
      ctx.stroke();

      // Camera zoom the world relative to player
      // For each entity from provider: world coord (x,y) → minimap (mx, my)
      //   mx = half + (x - player.x) * scale
      //   my = half + (y - player.y) * scale
      if (typeof provider === "function") {
        for (const e of provider()) {
          const ent = e as {
            kind?: string;
            position?: { x: number; y: number };
            localControlled?: true;
          };
          if (!ent.position || ent.localControlled) continue;
          const mx = half + (ent.position.x - player.x) * scale;
          const my = half + (ent.position.y - player.y) * scale;
          if (mx < 0 || mx > size || my < 0 || my > size) continue;
          const color = KIND_COLORS[ent.kind ?? ""] ?? "#cccccc";
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(mx, my, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Player at the center (always)
      ctx.fillStyle = "#60a5fa";
      ctx.strokeStyle = "#0b0d12";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(half, half, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      ctx.restore();

      // Disc border on top
      ctx.strokeStyle = "rgba(255,255,255,0.2)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(half, half, half - 0.5, 0, Math.PI * 2);
      ctx.stroke();

      // Compass label
      ctx.fillStyle = "rgba(255,255,255,0.45)";
      ctx.font = "10px ui-monospace, monospace";
      ctx.textAlign = "center";
      ctx.fillText("N", half, 14);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [game, size, worldRadius, bg]);

  if (!game) return null;
  return (
    <div style={wrapStyle(size)} data-testid="world-minimap">
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
    </div>
  );
}
